import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Footer } from "@/components/layout/Footer";
import { PricingSection } from "@/components/pricing/PricingSection";
import { SEO } from "@/components/seo/SEO";
import { supabase } from "@/integrations/supabase/client";

// Public page: Paddle's reviewers must be able to see prices without signing in.
const PublicShell = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen flex flex-col bg-background">
    <header className="border-b">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="font-semibold text-foreground">Veylo AI</Link>
        <Link to="/auth?redirect=/pricing" className="text-sm text-primary">Sign in</Link>
      </div>
    </header>
    <main className="flex-1 px-4 py-10">{children}</main>
    <Footer />
  </div>
);

const Pricing = () => {
  const [businessType, setBusinessType] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        setSignedIn(!!user);
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("business_size")
            .eq("id", user.id)
            .maybeSingle();
          if (profile?.business_size) setBusinessType(profile.business_size);
        }
      } catch (error) {
        console.error("Error loading profile:", error);
        setSignedIn(false);
      }
    })();
  }, []);

  if (signedIn === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const content = (
    <>
      <SEO title="Pricing — Veylo AI" description="Veylo AI plans: Starter $29, Growth $79, Pro $199 per month. Cancel anytime, 30-day money-back guarantee." path="/pricing" />
      <div className="space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-xl text-muted-foreground">Monthly plans in USD. Cancel anytime. 30-day money-back guarantee.</p>
        </div>
        <PricingSection businessType={businessType} />
      </div>
    </>
  );

  return signedIn ? <MainLayout>{content}</MainLayout> : <PublicShell>{content}</PublicShell>;
};

export default Pricing;
